import { useQuery } from "react-query";
import instance from "../../../api/axiosConfig";
import { QUERY_KEYS } from "../queryKeys";
import { logOutUser, generateToastError } from "../../../utils";

/** Build a stable query key from filters (primitives only for correct cache identity) */
const getAuditLogsQueryKey = (filters) => [
  QUERY_KEYS.AUDIT_LOGS,
  filters.page,
  filters.limit,
  filters.action,
  filters.statusCode,
  filters.startDate,
  filters.endDate,
  filters.userId,
  filters.resource,
  filters.sortBy,
  filters.sortOrder,
];

/**
 * Fetch audit logs with filters
 */
const fetchAuditLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.userId) params.append("userId", filters.userId);
  if (filters.action) params.append("action", filters.action);
  if (filters.resource) params.append("resource", filters.resource);
  if (filters.statusCode) params.append("statusCode", filters.statusCode);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  if (filters.page) params.append("page", filters.page);
  if (filters.limit) params.append("limit", filters.limit);
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
  
  const { data } = await instance.get(`/api/audit-logs?${params.toString()}`);
  return data;
};

/**
 * Hook to fetch all audit logs (admin only)
 */
export const useAuditLogs = (filters = {}, options = {}) => {
  const { navigate, toast, ...queryOptions } = options;
  return useQuery(
    getAuditLogsQueryKey(filters),
    () => fetchAuditLogs(filters),
    {
      enabled: true,
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (e) => {
        if (e?.response?.status === 401 && navigate) logOutUser(navigate);
        else if (toast) generateToastError(toast, e);
      },
      ...queryOptions,
    }
  );
};

/** Stable query key for my audit logs */
const getMyAuditLogsQueryKey = (filters) => [
  QUERY_KEYS.MY_AUDIT_LOGS,
  filters.page,
  filters.limit,
  filters.startDate,
  filters.endDate,
];

/**
 * Fetch current user's audit logs
 */
const fetchMyAuditLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  if (filters.page) params.append("page", filters.page);
  if (filters.limit) params.append("limit", filters.limit);
  
  const { data } = await instance.get(`/api/audit-logs/me?${params.toString()}`);
  return data;
};

/**
 * Hook to fetch current user's audit logs
 */
export const useMyAuditLogs = (filters = {}, options = {}) => {
  const { navigate, toast, ...queryOptions } = options;
  return useQuery(
    getMyAuditLogsQueryKey(filters),
    () => fetchMyAuditLogs(filters),
    {
      enabled: true,
      staleTime: 30000,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (e) => {
        if (e?.response?.status === 401 && navigate) logOutUser(navigate);
        else if (toast) generateToastError(toast, e);
      },
      ...queryOptions,
    }
  );
};

/** Stable query key for audit log stats */
const getAuditLogStatsQueryKey = (filters) => [
  QUERY_KEYS.AUDIT_LOG_STATS,
  filters.startDate,
  filters.endDate,
];

/**
 * Fetch audit log statistics
 */
const fetchAuditLogStats = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  
  const { data } = await instance.get(`/api/audit-logs/stats?${params.toString()}`);
  return data;
};

/**
 * Hook to fetch audit log statistics
 */
export const useAuditLogStats = (filters = {}, options = {}) => {
  const { navigate, toast, ...queryOptions } = options;
  return useQuery(
    getAuditLogStatsQueryKey(filters),
    () => fetchAuditLogStats(filters),
    {
      enabled: true,
      staleTime: 60000, // 1 minute
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (e) => {
        if (e?.response?.status === 401 && navigate) logOutUser(navigate);
        else if (toast) generateToastError(toast, e);
      },
      ...queryOptions,
    }
  );
};

/** Stable query key for failed login attempts */
const getFailedLoginAttemptsQueryKey = (filters) => [
  QUERY_KEYS.FAILED_LOGIN_ATTEMPTS,
  filters.page,
  filters.limit,
  filters.startDate,
  filters.endDate,
];

/**
 * Fetch failed login attempts
 */
const fetchFailedLoginAttempts = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  if (filters.page) params.append("page", filters.page);
  if (filters.limit) params.append("limit", filters.limit);
  
  const { data } = await instance.get(`/api/audit-logs/failed-logins?${params.toString()}`);
  return data;
};

/**
 * Hook to fetch failed login attempts
 */
export const useFailedLoginAttempts = (filters = {}, options = {}) => {
  const { navigate, toast, ...queryOptions } = options;
  return useQuery(
    getFailedLoginAttemptsQueryKey(filters),
    () => fetchFailedLoginAttempts(filters),
    {
      enabled: true,
      staleTime: 30000,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (e) => {
        if (e?.response?.status === 401 && navigate) logOutUser(navigate);
        else if (toast) generateToastError(toast, e);
      },
      ...queryOptions,
    }
  );
};

/** Stable query key for user audit logs */
const getUserAuditLogsQueryKey = (userId, filters) => [
  QUERY_KEYS.USER_AUDIT_LOGS,
  userId,
  filters.page,
  filters.limit,
  filters.startDate,
  filters.endDate,
];

/**
 * Fetch user-specific audit logs
 */
const fetchUserAuditLogs = async (userId, filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  if (filters.page) params.append("page", filters.page);
  if (filters.limit) params.append("limit", filters.limit);
  
  const { data } = await instance.get(`/api/audit-logs/user/${userId}?${params.toString()}`);
  return data;
};

/**
 * Hook to fetch specific user's audit logs
 */
export const useUserAuditLogs = (userId, filters = {}, options = {}) => {
  const { navigate, toast, ...queryOptions } = options;
  return useQuery(
    getUserAuditLogsQueryKey(userId, filters),
    () => fetchUserAuditLogs(userId, filters),
    {
      enabled: !!userId,
      staleTime: 30000,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (e) => {
        if (e?.response?.status === 401 && navigate) logOutUser(navigate);
        else if (toast) generateToastError(toast, e);
      },
      ...queryOptions,
    }
  );
};

export default {
  useAuditLogs,
  useMyAuditLogs,
  useAuditLogStats,
  useFailedLoginAttempts,
  useUserAuditLogs,
};
